import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";

import { api, extractApiError } from "../../../lib/api";
import { canAccessPublicForms } from "../../../lib/capabilities";
import { normalizePublicFormFieldConfig } from "../../../pages/publicPageContent";
import {
  buildLeadSourceDefault,
  DEFAULT_FORM_KIND,
  formatAllowedOriginsInput,
  getDefaultPlacementPurposeKey,
  parseAllowedOriginsInput,
} from "./publicFormsConfig";

function normalizeForm(form) {
  if (!form) {
    return null;
  }
  return {
    ...form,
    field_config: normalizePublicFormFieldConfig(form.field_config),
  };
}

function normalizePlacement(placement) {
  if (!placement) {
    return null;
  }
  const allowedOrigins = Array.isArray(placement.allowed_origins) ? placement.allowed_origins : [];
  return {
    ...placement,
    allowed_origins: allowedOrigins,
    allowed_origins_text: formatAllowedOriginsInput(allowedOrigins),
    submit_test_mode: Boolean(placement.submit_test_mode),
  };
}

function buildFormPayload(form) {
  return {
    name: form.name || "",
    form_kind: form.form_kind || DEFAULT_FORM_KIND,
    lead_source_default: form.lead_source_default || buildLeadSourceDefault(form.form_kind),
    is_active: Boolean(form.is_active),
    headline: form.headline || "",
    subline: form.subline || "",
    intro_text: form.intro_text || "",
    button_text: form.button_text || "",
    thank_you_title: form.thank_you_title || "",
    thank_you_text: form.thank_you_text || "",
    privacy_checkbox_text: form.privacy_checkbox_text || "",
    email_tracking_consent_enabled: Boolean(form.email_tracking_consent_enabled),
    email_tracking_consent_text: form.email_tracking_consent_text || "",
    marketing_consent_enabled: Boolean(form.marketing_consent_enabled),
    marketing_consent_text: form.marketing_consent_text || "",
    privacy_policy_url: form.privacy_policy_url || "",
    imprint_url: form.imprint_url || "",
    legal_notice_text: form.legal_notice_text || "",
    field_config: form.field_config || [],
  };
}

function buildPlacementPayload(placement) {
  return {
    name: placement.name || "",
    purpose_key: placement.purpose_key || DEFAULT_FORM_KIND,
    source_label: placement.source_label || "",
    cta_label: placement.cta_label || "",
    asset_key: placement.asset_key || "",
    campaign_key: placement.campaign_key || "",
    embed_mode: placement.embed_mode || "standalone",
    allowed_origins: parseAllowedOriginsInput(placement.allowed_origins_text),
    submit_test_mode: Boolean(placement.submit_test_mode),
    is_active: placement.is_active !== false,
    notes: placement.notes || "",
  };
}

function buildEmptyPlacement(formKind = DEFAULT_FORM_KIND) {
  return {
    name: "",
    purpose_key: getDefaultPlacementPurposeKey(formKind),
    source_label: "",
    cta_label: "",
    asset_key: "",
    campaign_key: "",
    embed_mode: "standalone",
    allowed_origins: [],
    allowed_origins_text: "",
    submit_test_mode: false,
    is_active: true,
    notes: "",
  };
}

export default function usePublicFormsManager({ capabilities }) {
  const [loading, setLoading] = useState(true);
  const [forms, setForms] = useState([]);
  const [selectedFormId, setSelectedFormId] = useState("");
  const [selectedForm, setSelectedForm] = useState(null);
  const [placements, setPlacements] = useState([]);
  const [createName, setCreateName] = useState("");
  const [createKind, setCreateKind] = useState("demo_request");
  const [savingForm, setSavingForm] = useState(false);
  const [savingPlacement, setSavingPlacement] = useState(false);
  const [placementDialogOpen, setPlacementDialogOpen] = useState(false);
  const [placementDialogMode, setPlacementDialogMode] = useState("create");
  const [placementDraft, setPlacementDraft] = useState(buildEmptyPlacement());
  const selectedFormIdRef = useRef("");

  useEffect(() => {
    selectedFormIdRef.current = selectedFormId;
  }, [selectedFormId]);

  const publicFormsEnabled = canAccessPublicForms(capabilities);
  const formLimit = Number(capabilities?.max_public_forms ?? 0);
  const formCount = forms.length;
  const formLimitReached = formLimit > -1 && formCount >= formLimit;

  const createDisabledReason = useMemo(() => {
    if (!publicFormsEnabled) {
      return "Öffentliche Formulare und FyreFunnel sind für diesen Mandanten nicht freigeschaltet.";
    }
    if (formLimit < 1) {
      return "Im aktuellen Tarif können keine neuen Formulare erstellt werden.";
    }
    if (formLimitReached) {
      return `Ihr aktuelles Limit ist erreicht: ${formCount} von ${formLimit} Formularen.`;
    }
    if (!createName.trim()) {
      return "Bitte zuerst einen Formularnamen eingeben.";
    }
    return "";
  }, [createName, formCount, formLimit, formLimitReached, publicFormsEnabled]);

  const loadFormDetails = useCallback(async (formId) => {
    if (!formId) {
      setSelectedFormId("");
      setSelectedForm(null);
      setPlacements([]);
      return;
    }
    const [formResponse, placementsResponse] = await Promise.all([
      api.get(`/api/settings/public-forms/${formId}`),
      api.get(`/api/settings/public-forms/${formId}/placements`),
    ]);
    setSelectedFormId(formId);
    setSelectedForm(normalizeForm(formResponse.data));
    setPlacements((placementsResponse.data || []).map(normalizePlacement));
  }, []);

  const loadForms = useCallback(async () => {
    if (!publicFormsEnabled) {
      setForms([]);
      setSelectedFormId("");
      setSelectedForm(null);
      setPlacements([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const response = await api.get("/api/settings/public-forms");
      const nextForms = response.data || [];
      setForms(nextForms);
      if (!nextForms.length) {
        setSelectedFormId("");
        setSelectedForm(null);
        setPlacements([]);
        return;
      }
      const preferredFormId = nextForms.some((form) => form.id === selectedFormIdRef.current)
        ? selectedFormIdRef.current
        : nextForms[0].id;
      await loadFormDetails(preferredFormId);
    } catch (error) {
      toast.error(extractApiError(error, "Formulare konnten nicht geladen werden."));
    } finally {
      setLoading(false);
    }
  }, [loadFormDetails, publicFormsEnabled]);

  useEffect(() => {
    loadForms();
  }, [loadForms]);

  const updateFormsList = useCallback((nextForm) => {
    setForms((current) => {
      const existing = current.find((form) => form.id === nextForm.id);
      if (!existing) {
        return [...current, nextForm];
      }
      return current.map((form) => (form.id === nextForm.id ? { ...form, ...nextForm } : form));
    });
  }, []);

  const handleCreateForm = async () => {
    if (createDisabledReason) {
      return;
    }
    try {
      const response = await api.post("/api/settings/public-forms", {
        name: createName.trim(),
        form_kind: createKind,
        lead_source_default: buildLeadSourceDefault(createKind),
        privacy_checkbox_text: "Ich habe die Datenschutzerklärung gelesen.",
      });
      const createdForm = normalizeForm(response.data);
      updateFormsList(createdForm);
      setCreateName("");
      await loadFormDetails(createdForm.id);
      toast.success("Formular angelegt.");
    } catch (error) {
      toast.error(extractApiError(error, "Formular konnte nicht angelegt werden."));
    }
  };

  const handleSelectForm = async (formId) => {
    try {
      await loadFormDetails(formId);
    } catch (error) {
      toast.error(extractApiError(error, "Formulardetails konnten nicht geladen werden."));
    }
  };

  const handleSaveForm = async () => {
    if (!selectedForm) {
      return;
    }
    setSavingForm(true);
    try {
      const response = await api.patch(
        `/api/settings/public-forms/${selectedForm.id}`,
        buildFormPayload(selectedForm),
      );
      const nextForm = normalizeForm(response.data);
      setSelectedForm(nextForm);
      updateFormsList(nextForm);
      toast.success("Formular gespeichert.");
    } catch (error) {
      toast.error(extractApiError(error, "Formular konnte nicht gespeichert werden."));
    } finally {
      setSavingForm(false);
    }
  };

  const openPlacementCreateDialog = () => {
    setPlacementDialogMode("create");
    setPlacementDraft(buildEmptyPlacement(selectedForm?.form_kind));
    setPlacementDialogOpen(true);
  };

  const openPlacementEditDialog = (placement) => {
    setPlacementDialogMode("edit");
    setPlacementDraft({ ...placement });
    setPlacementDialogOpen(true);
  };

  const handleSavePlacement = async () => {
    if (!selectedFormId) {
      return;
    }
    setSavingPlacement(true);
    try {
      if (placementDialogMode === "edit" && placementDraft.id) {
        const response = await api.patch(
          `/api/settings/public-form-placements/${placementDraft.id}`,
          buildPlacementPayload(placementDraft),
        );
        setPlacements((current) => current.map((item) => (
          item.id === response.data.id ? normalizePlacement(response.data) : item
        )));
        toast.success("Placement gespeichert.");
      } else {
        const response = await api.post(
          `/api/settings/public-forms/${selectedFormId}/placements`,
          buildPlacementPayload(placementDraft),
        );
        setPlacements((current) => [...current, normalizePlacement(response.data)]);
        setForms((current) => current.map((form) => (
          form.id === selectedFormId
            ? { ...form, placement_count: Number(form.placement_count || 0) + 1 }
            : form
        )));
        setSelectedForm((current) => (
          current ? { ...current, placement_count: Number(current.placement_count || 0) + 1 } : current
        ));
        toast.success("Placement angelegt.");
      }
      setPlacementDialogOpen(false);
      setPlacementDraft(buildEmptyPlacement(selectedForm?.form_kind));
    } catch (error) {
      toast.error(extractApiError(error, "Placement konnte nicht gespeichert werden."));
    } finally {
      setSavingPlacement(false);
    }
  };

  return {
    loading,
    forms,
    selectedFormId,
    selectedForm,
    placements,
    createName,
    createKind,
    savingForm,
    savingPlacement,
    placementDialogOpen,
    placementDialogMode,
    placementDraft,
    publicFormsEnabled,
    formLimit,
    formCount,
    formLimitReached,
    createDisabledReason,
    setCreateName,
    setCreateKind,
    setSelectedForm,
    setPlacementDraft,
    handleCreateForm,
    handleSelectForm,
    handleSaveForm,
    openPlacementCreateDialog,
    openPlacementEditDialog,
    handleSavePlacement,
    closePlacementDialog: () => setPlacementDialogOpen(false),
  };
}
